import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, StatCard, Loading } from "@/components/shared";
import { DateRange, presetRange, toApiRange } from "@/components/Filters";
import { Users, Repeat, PhoneOff, UserX, Clock, AlertTriangle, Calendar, CheckCircle2, GraduationCap, IndianRupee, TrendingUp } from "lucide-react";

function SourcePerformance() {
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState(presetRange("all"));
  const r = toApiRange(range);
  const { data } = useQuery({ queryKey: ["src-perf", r], queryFn: async () => (await api.get("/reports/sources", { params: { start: r.date_from, end: r.date_to } })).data });
  const rows = data?.sources || [];
  return (
    <div className="rounded-xl border bg-white dark:bg-slate-800 p-5 lg:col-span-2" data-testid="source-performance">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 font-head font-bold"><TrendingUp className="w-4 h-4 text-brand-blue" /> Source performance</div>
        <DateRange preset={preset} setPreset={setPreset} range={range} setRange={setRange} testid="src-range" />
      </div>
      {!rows.length ? <p className="text-slate-400 text-sm">No leads in this range</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500"><tr><th className="text-left py-2">Source</th><th className="text-left py-2">Leads</th><th className="text-left py-2">Admissions</th><th className="text-left py-2">Conversion</th></tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.source} className="border-t"><td className="py-2 font-medium capitalize">{s.source}</td><td className="py-2">{s.leads}</td><td className="py-2 text-violet-600">{s.admissions}</td>
                  <td className="py-2"><span className={`font-semibold ${s.conversion >= 10 ? "text-emerald-600" : s.conversion > 0 ? "text-amber-600" : "text-slate-400"}`}>{s.conversion}%</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const { data, isLoading } = useQuery({ queryKey: ["reports"], queryFn: async () => (await api.get("/reports/overview")).data });
  if (isLoading) return <Loading />;

  return (
    <div>
      <PageHeader title="Reports & Owner Dashboard" subtitle="Activity vs conversion, kept separate. Each metric is defined below." testid="reports-header" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Unique leads" value={data.unique_leads} icon={Users} tone="blue" testid="r-unique" />
        <StatCard label="Repeat enquiry events" value={data.repeat_events} icon={Repeat} tone="slate" testid="r-repeat" />
        <StatCard label="Uncontacted" value={data.uncontacted} icon={PhoneOff} tone="orange" testid="r-uncontacted" />
        <StatCard label="Unassigned" value={data.unassigned} icon={UserX} tone="red" testid="r-unassigned" />
        <StatCard label="Follow-ups due" value={data.followups_due} icon={Clock} tone="blue" testid="r-fdue" />
        <StatCard label="Follow-ups overdue" value={data.followups_overdue} icon={AlertTriangle} tone="red" testid="r-foverdue" />
        <StatCard label="Booked visits" value={data.booked_visits} icon={Calendar} tone="orange" testid="r-booked" />
        <StatCard label="Attended visits" value={data.attended_visits} icon={CheckCircle2} tone="green" testid="r-attended" />
        <StatCard label="Confirmed admissions" value={data.confirmed_admissions} icon={GraduationCap} tone="blue" testid="r-confirmed" />
        <StatCard label="Paid admissions" value={data.paid_admissions} icon={IndianRupee} tone="green" testid="r-paid" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <SourcePerformance />
        <div className="rounded-xl border bg-white dark:bg-slate-800 p-5">
          <div className="font-head font-bold mb-3">Counsellor productivity</div>
          <table className="w-full text-sm"><thead className="text-xs uppercase text-slate-500"><tr><th className="text-left py-2">Name</th><th className="text-left py-2">Leads</th><th className="text-left py-2">Enrolled</th><th className="text-left py-2">Overdue</th></tr></thead>
            <tbody>{data.staff.map((s) => <tr key={s.name} className="border-t"><td className="py-2 font-medium">{s.name}</td><td className="py-2">{s.leads}</td><td className="py-2 text-emerald-600">{s.enrolled}</td><td className="py-2 text-red-600">{s.overdue}</td></tr>)}{!data.staff.length && <tr><td colSpan="4" className="py-3 text-slate-400 text-center">No sales staff yet</td></tr>}</tbody>
          </table>
        </div>

        <div className="rounded-xl border bg-white dark:bg-slate-800 p-5">
          <div className="font-head font-bold mb-3">Batch occupancy</div>
          {!data.batches.length ? <p className="text-slate-400 text-sm">No batches</p> : data.batches.map((b) => (
            <div key={b.name} className="mb-3"><div className="flex justify-between text-sm"><span>{b.name}</span><span>{b.enrolled}/{b.capacity} ({b.occupancy}%)</span></div>
              <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full mt-1"><div className="h-full bg-brand-blue rounded-full" style={{ width: `${Math.min(100, b.occupancy)}%` }} /></div></div>
          ))}
        </div>

        <div className="rounded-xl border bg-white dark:bg-slate-800 p-5">
          <div className="font-head font-bold mb-3">Lost reasons</div>
          {Object.keys(data.lost_reasons).length === 0 ? <p className="text-slate-400 text-sm">No lost leads</p> : Object.entries(data.lost_reasons).map(([r, c]) => (
            <div key={r} className="flex justify-between text-sm border-b py-1.5"><span className="capitalize">{r.replace(/_/g, " ")}</span><b>{c}</b></div>
          ))}
        </div>

        <div className="rounded-xl border bg-white dark:bg-slate-800 p-5 text-sm">
          <div className="font-head font-bold mb-3">Metric definitions</div>
          {Object.entries(data.definitions).map(([k, v]) => <div key={k} className="mb-2"><b className="capitalize">{k.replace(/_/g, " ")}:</b> <span className="text-slate-500">{v}</span></div>)}
          <p className="text-xs text-slate-400 mt-3">Acquisition cost = campaign spend ÷ attributed paid admissions. Shows "Expense not recorded" or "No paid admissions" where applicable. Collected fees are not shown as profit.</p>
        </div>
      </div>
    </div>
  );
}
